/**
 * @pj/rules — pure, deterministic game logic over the world + abilities.
 *
 * `canEnter` is the single traversal verdict the movement system asks. Abilities
 * fold in here (surfing makes water passable, etc.) — the engine never learns
 * what "surf" means; it only renders. This is the seam every field-move (Dive,
 * Waterfall, Cut…) extends: add an ability flag + a clause here, nothing else.
 */
import { tileDefAt, type MapData } from "@pj/content";
import type { Dir } from "@pj/protocol";
import { DIR_VECTORS } from "@pj/protocol";

export interface TraversalCtx {
  /** Riding the surf mount: water is passable, and moving onto land dismounts. */
  surfing: boolean;
  /** Open scripted doors become enterable (they warp). */
  openDoors?: ReadonlySet<string> | undefined;
  /** Door id at a tile, provided by the sim (doors are level data, not map data). */
  doorIdAt?: ((tx: number, ty: number) => string | null) | undefined;
}

export const NO_ABILITIES: TraversalCtx = { surfing: false };

export function canEnter(map: MapData, tx: number, ty: number, ctx: TraversalCtx): boolean {
  const def = tileDefAt(map, tx, ty);
  if (!def) return false; // out of bounds
  switch (def.behavior) {
    case "blocked":
      return false;
    case "water":
      return ctx.surfing;
    case "door": {
      const id = ctx.doorIdAt?.(tx, ty) ?? null;
      return id !== null && (ctx.openDoors?.has(id) ?? false);
    }
    case "ledge_down":
      return true; // one-way handling lands later; passable for now
    case "grass":
    case "normal":
      return true; // land is always enterable; entering it while surfing dismounts
  }
}

export function isWater(map: MapData, tx: number, ty: number): boolean {
  return tileDefAt(map, tx, ty)?.behavior === "water";
}

export function isLand(map: MapData, tx: number, ty: number): boolean {
  const b = tileDefAt(map, tx, ty)?.behavior;
  return b === "normal" || b === "grass" || b === "ledge_down";
}

/**
 * The Gen-3 surf-initiation verdict (pokeemerald `IsPlayerFacingSurfableWater`):
 * the player must be standing on land, not already surfing, and the tile
 * directly ahead must be surfable water. Ability possession is the sim's check.
 */
export function canStartSurf(
  map: MapData,
  tx: number,
  ty: number,
  facing: Dir,
  ctx: TraversalCtx,
): boolean {
  if (ctx.surfing) return false;
  if (!isLand(map, tx, ty)) return false;
  const v = DIR_VECTORS[facing];
  return isWater(map, tx + v.dx, ty + v.dy);
}

// --- deterministic RNG (mulberry32) ----------------------------------------
// All randomness (encounters, capture, loot, daily picks) must run through a
// seeded RNG so single-player is reproducible and a future PvP sim stays in sync.

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 32-bit hash of a string — handy for seeding per-level/per-day RNG. */
export function hashSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
